import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Mail, Shield } from "lucide-react";
import { useAccount } from "@/features/account/hooks/useAccount";
import { AccessKeysSection } from "@/features/account/components/AccessKeysSection";
import { CliDevicesSection } from "@/features/cli/components/CliDevicesSection";
import { ChangePassword } from "@/features/account/components/ChangePassword";
import { DeleteAccountSection } from "@/features/account/components/DeleteAccountSection";
import { LogoutAllSessions } from "@/features/account/components/LogoutAllSessions";
import { PasskeysSection } from "@/features/account/components/PasskeysSection";
import { TotpSection } from "@/features/account/components/TotpSection";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { PageLoader } from "@/components/ui/PageLoader";

export const Route = createFileRoute("/_authenticated/account")({
  component: AccountPage,
});

function AccountPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useAccount();

  const account = data?.account;

  return (
    <div className="mx-auto max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-heading">{t("account.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("account.subtitle")}</p>
      </div>

      {/* Loading */}
      {isLoading && <PageLoader />}

      {/* Error */}
      <ApiErrorBanner error={error} className="mt-8" />

      {account && (
        <div className="mt-6 space-y-6">
          {/* Profile card */}
          <div className="rounded-xl border border-border bg-surface p-6">
            <h3 className="text-base font-semibold text-heading">{t("account.profile")}</h3>

            <div className="mt-4 space-y-4">
              {/* Avatar + Name */}
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo to-cyan">
                  <span className="text-2xl font-bold text-white">
                    {account.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-lg font-semibold text-heading">{account.name}</p>
                  <p className="flex items-center gap-1.5 text-sm text-muted">
                    <Mail size={14} />
                    {account.email}
                  </p>
                </div>
              </div>

              {/* Fields */}
              <div className="grid gap-4 rounded-lg bg-elevated/50 p-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted">{t("account.name")}</p>
                  <p className="mt-0.5 text-sm font-medium text-heading">{account.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted">{t("account.email")}</p>
                  <p className="mt-0.5 text-sm font-medium text-heading">{account.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="rounded-xl border border-border bg-surface p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-heading">
              <Shield size={18} />
              {t("account.security")}
            </h3>

            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between rounded-lg bg-elevated/50 p-4">
                <div>
                  <p className="text-sm font-medium text-heading">{t("account.changePassword")}</p>
                </div>
                <ChangePassword />
              </div>

              <div className="flex items-center justify-between rounded-lg bg-elevated/50 p-4">
                <div>
                  <p className="text-sm font-medium text-heading">{t("account.sessions")}</p>
                </div>
                <LogoutAllSessions />
              </div>
            </div>
          </div>

          <PasskeysSection />

          <TotpSection />

          <CliDevicesSection />

          <AccessKeysSection />

          <DeleteAccountSection />
        </div>
      )}
    </div>
  );
}
